import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import SearchBar from '../components/SearchBar'
import Newsletter from '../components/Newsletter'
import CodeCard from '../components/CodeCard'
import Faq from '../components/Faq'
import SchemaMarkup from '../components/SchemaMarkup'
import supabase from '../services/supabase'
import SponsorBanner from '../components/SponsorBanner'
import '../styles/global.css'

const DirectoryCnae2009Page = () => {
  const [cnaeCodes, setCnaeCodes] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [limit, setLimit] = useState(20)
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const navigate = useNavigate()

  const faqData = [
    {
      question: '¿Qué es el CNAE 2009?',
      answer:
        'La Clasificación Nacional de Actividades Económicas (CNAE) es un sistema utilizado en España para clasificar y codificar las actividades económicas realizadas por empresas y autónomos. Esta clasificación facilita la recopilación de datos estadísticos, facilita la gestión fiscal y permite una adecuada categorización de las actividades económicas a nivel nacional.\n\nLa CNAE 2009 fue aprobada mediante el Real Decreto 475/2007, de 13 de abril, publicado en el Boletín Oficial del Estado (BOE) número 102, de 28 de abril de 2007. Esta clasificación entró en vigor el 1 de enero de 2009, sustituyendo a la versión anterior, CNAE-93.',
    },
    {
      question:
        '¿Cuáles son las principales diferencias entre la CNAE 2009 y la CNAE 2025?',
      answer:
        'Las principales diferencias entre la CNAE 2009 y la CNAE 2025 son:\n\nMayor número de clases: La CNAE 2025 incluye 664 clases, en comparación con las 629 clases de la CNAE 2009.\nReestructuración de algunas actividades: Se han creado nuevas clases para actividades emergentes, como el comercio electrónico, plataformas digitales, y sectores vinculados con la sostenibilidad y las energías renovables.\nModificaciones en divisiones específicas: Algunas actividades, como las relacionadas con la fabricación de productos y la industria del reciclaje, han sido reagrupadas para reflejar de forma más precisa las tendencias económicas actuales.',
    },
    {
      question:
        '¿Cómo afecta a las empresas la transición de la CNAE 2009 a la CNAE 2025?',
      answer:
        'La transición de la CNAE 2009 a la CNAE 2025 implica que las empresas deberán actualizar su código CNAE si su actividad se ve modificada o si los cambios en la estructura del CNAE afectan a su clasificación. La actualización es importante para mantener la precisión en los trámites fiscales, administrativos y estadísticos. Las empresas tendrán hasta el 30 de junio de 2025 para actualizar su código CNAE ante la Seguridad Social.',
    },
    {
      question:
        '¿Dónde puedo consultar el código CNAE de mi actividad económica?',
      answer:
        'Para identificar el código CNAE correcto para tu actividad económica:\n\nUtiliza nuestro conversor introduciendo una descripción de tu actividad\nRevisa las sugerencias automáticas que te ofrece la herramienta\nExamina la descripción detallada de cada código para encontrar el que mejor se ajusta\nVerifica que el código seleccionado incluye todas las actividades que realizas\n\nEn caso de duda, puedes consultar con un asesor fiscal o directamente con la Agencia Tributaria.',
    },
    {
      question: '¿Qué plazo tiene mi empresa para actualizar el código CNAE?',
      answer:
        'Las empresas deben actualizar su código CNAE a más tardar el 30 de junio de 2025. Este plazo se establece para que las empresas puedan adaptar sus registros a la nueva clasificación antes de que se implemente de manera definitiva en los procesos administrativos y fiscales.',
    },
    {
      question: '¿Qué sucede si mi empresa no actualiza su código CNAE?',
      answer:
        'No actualizar el código CNAE puede llevar a inconsistencias en los registros fiscales y administrativos. Además, podría afectar el cálculo de impuestos, la obtención de beneficios fiscales y la correcta clasificación en estadísticas oficiales. Es fundamental que las empresas mantengan su código CNAE actualizado para cumplir con los requisitos legales y fiscales.',
    },
    {
      question: '¿Cómo puedo convertir un código CNAE 2009 a IAE?',
      answer:
        'Para convertir un código IAE a CNAE, simplemente utiliza nuestra herramienta de conversión. Ingresa el código IAE en el campo de búsqueda y selecciona el resultado correcto de la lista de sugerencias. Nuestro conversor te proporcionará el código CNAE correspondiente a tu actividad, asegurando que puedas cumplir con los requisitos fiscales y administrativos de manera eficiente.',
    },
    {
      question:
        '¿Cómo actualizo mi código CNAE 2009 al nuevo CNAE 2025 correspondiente?',
      answer:
        'La actualización de tu código CNAE al nuevo estándar CNAE 2025 es un proceso necesario para todas las empresas. Debes realizar esta actualización antes del 30 de junio de 2025. Puedes utilizar las tablas de correspondencia entre la CNAE 2025 y la CNAE 2009 proporcionadas por el Instituto Nacional de Estadística (INE) o, simplemente, utilizar nuestro conversor.',
    },
    {
      question:
        '¿Qué es la Tabla de correspondencia entre CNAE 2009 y CNAE 2025?',
      answer:
        'Las tablas de correspondencia son herramientas fundamentales para comparar datos estadísticos recopilados y presentados utilizando diferentes clasificaciones, como la CNAE-2025 y CNAE-2009. Su necesidad surge cuando se produce un cambio en la clasificación a lo largo del tiempo o cuando existen diferentes marcos subyacentes que impiden una estrecha relación entre clasificaciones. Las tablas de correspondencia entre diferentes versiones de la misma clasificación describen en detalle los cambios que se han producido en el proceso de revisión.',
    },
  ]

  const fetchCnaeCodes = async (
    limitParam = limit,
    offsetParam = offset,
    append = false
  ) => {
    try {
      setIsLoading(true)
      const { data, error } = await supabase.rpc(
        'get_most_searched_cnae_2009_codes',
        {
          limit_param: limitParam,
          offset_param: offsetParam,
        }
      )

      if (error) throw error

      if (!data || data.length === 0) {
        console.warn('No CNAE 2009 codes found')
        setHasMore(false)
        if (!append) {
          setCnaeCodes([])
        }
        return
      }

      // If we're appending, add to existing codes, otherwise replace
      if (append) {
        setCnaeCodes((prevCodes) => [...prevCodes, ...data])
      } else {
        setCnaeCodes(data)
      }

      // Check if we have more data to load
      setHasMore(data.length === limitParam)
    } catch (error) {
      console.error('Error fetching CNAE 2009 codes:', error)
      setError('Error al cargar los códigos CNAE 2009')
    } finally {
      setIsLoading(false)
    }
  }

  // Load more codes
  const handleLoadMore = () => {
    const newOffset = offset + limit
    setOffset(newOffset)
    fetchCnaeCodes(limit, newOffset, true)
  }

  useEffect(() => {
    fetchCnaeCodes()
  }, [])

  return (
    <div className='container mx-auto px-4'>
      {/* Header Section */}
      <header className='py-8 md:py-16 text-center'>
        <h1 className='text-2xl md:text-4xl font-bold mb-4 text-text-primary'>
          Listado de Códigos CNAE 2009 Actualizado
        </h1>
        <p className='text-text-secondary text-lg mb-8 max-w-2xl mx-auto'>
          Explora la base de datos completa de códigos CNAE 2009 (Clasificación
          Nacional de Actividades Económicas). Encuentra, filtra y consulta en
          tiempo real los códigos oficiales con sus descripciones detalladas y
          equivalencias IAE.
        </p>

        {/* Search Bar */}
        <div className='max-w-xl mx-auto mb-6'>
          <SearchBar
            type='cnae2009'
            hideConvertButton={true}
            onCodeSelect={(code) => {
              // Redirect to details page when a code is selected
              navigate(`/cnae-2009/${code.code}/${code.slug}`)
            }}
          />
        </div>

        {/* Sponsor Banner */}
        <SponsorBanner />
      </header>

      {/* CNAE 2009 Codes Section */}
      <section className='py-8 md:py-12'>
        <h2 className='text-xl md:text-3xl font-bold mb-6 text-text-primary text-center'>
          Códigos CNAE 2009 Populares
        </h2>

        {/* SearchResultsPage Schema */}
        {cnaeCodes.length > 0 && (
          <SchemaMarkup
            type='SearchResultsPage'
            data={{
              results: cnaeCodes.map((code) => ({
                title: `${code.cod_cnae_2009} - ${code.titulo_cnae_2009}`,
                url: `https://conversoriaecnae.es/cnae-2009/${code.cod_cnae_2009.replace(
                  '.',
                  ''
                )}/${code.slug_cnae_2009}`,
              })),
            }}
          />
        )}

        {/* ItemList Schema */}
        {cnaeCodes.length > 0 && (
          <SchemaMarkup
            type='ItemList'
            data={{
              name: 'Códigos CNAE 2009 Populares',
              description:
                'Lista de los códigos CNAE 2009 más buscados en España',
              items: cnaeCodes.map((code) => ({
                title: `${code.cod_cnae_2009} - ${code.titulo_cnae_2009}`,
                code: code.cod_cnae_2009,
                url: `https://conversoriaecnae.es/cnae-2009/${code.cod_cnae_2009.replace(
                  '.',
                  ''
                )}/${code.slug_cnae_2009}`,
              })),
            }}
          />
        )}

        {isLoading && cnaeCodes.length === 0 ? (
          <div className='flex justify-center'>
            <div className='w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin'></div>
          </div>
        ) : error ? (
          <div className='text-center text-red-500'>{error}</div>
        ) : (
          <>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
              {cnaeCodes.map((code, index) => (
                <CodeCard
                  key={index}
                  code={code.cod_cnae_2009}
                  title={code.titulo_cnae_2009}
                  type='cnae2009'
                  slug={code.slug_cnae_2009}
                  searchCount={code.cant_busquedas_cnae_2009}
                />
              ))}
            </div>

            {/* Load More Button */}
            {hasMore && cnaeCodes.length > 0 && (
              <div className='flex justify-center mt-8'>
                <button
                  onClick={handleLoadMore}
                  className='px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors duration-300 flex items-center'
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className='mr-2'>Cargando...</span>
                      <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin'></div>
                    </>
                  ) : (
                    'Cargar Más Códigos'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Newsletter Section */}
      <Newsletter />

      {/* FAQ Section */}
      <Faq
        title='Preguntas Frecuentes sobre CNAE 2009'
        description='Resolviendo tus dudas sobre la Clasificación Nacional de Actividades Económicas 2009'
        faqData={faqData}
      />
    </div>
  )
}

export default DirectoryCnae2009Page
